import { CLEAR_DAY, PROGRESS_EVERY_SECONDS, missionReward, type FinishRunResponse } from '@accra/shared';
import type { WebGLRenderer } from 'three';
import { unlockAudioOnGesture } from '../audio/audio';
import { bindHotkeys } from './hotkeys';
import { EngineSound } from '../audio/engine';
import { playHorn } from '../audio/horns';
import { playFuelWarning } from '../audio/fuelWarning';
import { Radio } from '../audio/radio/radio';
import { createRadioBar } from '../ui/hud/radioBar';
import { PLAYER_VEHICLES, type PlayerVehicleId } from '../content/playerVehicles';
import { SHUFFLE_ROUTE_IDS, routeById } from '../content/routes';
import { shuffleRoute } from '../meta/routeShuffle';
import { ChallengeFlow } from './challengeFlow';
import { planRun, type RunKind, type RunPlan } from './runPlan';
import { shareScoreCard } from './scoreCardFlow';
import { garageActions, meLoaders } from './screenHooks';
import { RivalTicker } from '../ui/hud/rivalTicker';
import { Me } from '../ui/screens/Me';
import { emptyActions, InputState } from '../core/input/actions';
import { bindKeyboard } from '../core/input/keyboard';
import { bindSwipes } from '../core/input/touch';
import { Loop } from '../core/loop';
import { PLAYER_SPECS } from '../entities/vehicles/catalog';
import { Run, type RunVehicle } from '../game/Run';
import { RunView } from '../game/RunView';
import { loadProfile, saveProfile, type Profile } from '../meta/profile';
import { setName } from '../net/garage';
import { fetchBoard, fetchChampion } from '../net/leaderboards';
import { ShiftSync } from '../net/shiftSync';
import { fetchPlacements, reportImpressions } from '../net/ads';
import { PerfGovernor } from '../render/perfTier';
import { createRenderer } from '../render/renderer';
import { Feedback } from '../ui/hud/feedback';
import { Hud } from '../ui/hud/Hud';
import { SignalPrompt } from '../ui/hud/signalPrompt';
import { StopPrompt } from '../ui/hud/stopPrompt';
import { DashPrompt } from '../ui/hud/dashPrompt';
import { PowerChips } from '../ui/hud/powerChips';
import { createTouchPedals } from '../ui/hud/touchPedals';
import { Garage } from '../ui/screens/Garage';
import { GameOver } from '../ui/screens/GameOver';
import { Leaders } from '../ui/screens/Leaders';
import { Menu, type MenuActions } from '../ui/screens/Menu';
import { MissionsPanel } from '../ui/screens/missionsPanel';
import { Pause } from '../ui/screens/Pause';
import { setActiveRoute } from '../world/route';
import { GarageController, garageState, trotroLook } from './garageController';
import { upgradeLevels } from '../meta/garage';
import { trafficDensity } from '../content/trafficLevels';
import { settleShift } from './settleShift';

type Mode = 'menu' | 'garage' | 'leaders' | 'badges' | 'running' | 'paused' | 'crashed' | 'over';

/** After a breakdown, the scene holds this long before the shift summary. */
const CRASH_PAUSE = 1.1;

/** Top-level state machine. The only place that knows about every module. */
export class App {
  private mode: Mode = 'menu';
  private run: Run;
  private readonly input = new InputState();
  private readonly renderer: WebGLRenderer;
  private readonly view: RunView;
  private readonly hud: Hud;
  private readonly prompt: StopPrompt;
  private readonly dashPrompt: DashPrompt;
  private readonly signal: SignalPrompt;
  private readonly powerChips: PowerChips;
  private readonly feedback: Feedback;
  private readonly menu: Menu;
  private readonly garage: Garage;
  private readonly leaders: Leaders;
  private readonly pause: Pause;
  private readonly over: GameOver;
  private readonly me: Me;
  private readonly rival: RivalTicker;
  private readonly challenges: ChallengeFlow;
  /** How the shift in progress (or just finished) is being played. */
  private plan: RunPlan | null = null;
  /** The server's answer for the shift just finished (badges, ranks), for the score card. */
  private lastResult: FinishRunResponse | null = null;
  private readonly overMissions = new MissionsPanel();
  private readonly loop: Loop;
  private readonly perf: PerfGovernor;
  private readonly sync = new ShiftSync();
  private readonly radio = new Radio();
  private readonly engine = new EngineSound();
  private readonly garageControl: GarageController;
  private profile: Profile = loadProfile();
  private crashTimer = 0;
  /** Each shift is finished (and sent to the server) exactly once. */
  private shiftFinished = false;
  /** Game time (s) when the next live progress update is due. */
  private nextProgressAt = PROGRESS_EVERY_SECONDS;
  private lastRender = performance.now();

  constructor(canvas: HTMLCanvasElement, ui: HTMLElement) {
    let view: RunView | null = null;
    const handle = createRenderer(canvas, (aspect) => view?.chase.setAspect(aspect));
    this.renderer = handle.renderer;
    this.view = view = new RunView(this.renderer);
    this.perf = new PerfGovernor((tier) => {
      handle.setPixelRatioCap(tier.pixelRatioCap);
      this.view.setShadowQuality(tier.shadowMapSize);
    });
    this.view.chase.setAspect(window.innerWidth / window.innerHeight);
    this.hud = new Hud(ui);
    this.prompt = new StopPrompt(this.hud.root);
    this.signal = new SignalPrompt(this.hud.root);
    this.powerChips = new PowerChips(this.hud.root);
    this.feedback = new Feedback(this.hud.root);
    this.dashPrompt = new DashPrompt(this.hud.root, (choice) => this.run.answerDash(choice));
    this.rival = new RivalTicker(this.hud.root);
    createTouchPedals(this.hud.root, this.input);
    this.menu = new Menu(ui, this.menuActions());
    this.challenges = new ChallengeFlow(this.menu, () => this.profile.name);
    this.garageControl = new GarageController(this.garageDeps(), () => this.garage);
    this.garage = new Garage(ui, garageActions(this.garageControl, () => this.backToMenu()));
    this.leaders = new Leaders(ui, fetchBoard, (name) => this.saveName(name), () => this.backToMenu());
    this.pause = new Pause(ui, () => this.resume(), () => this.endShift(), () => this.toMenu());
    this.me = new Me(ui, meLoaders(this.sync, this.menu, this.challenges), () => this.backToMenu());
    this.over = new GameOver(
      ui, () => this.startRun(this.plan?.kind), () => this.toMenu(), (name) => this.saveName(name),
      async () => this.challenges.share(this.sync.key, await this.sync.runId()),
      () => shareScoreCard(this.view, this.run.state, this.plan, this.profile.routeId, this.profile.vehicle, this.lastResult),
    );
    this.over.missions.append(this.overMissions.root);
    bindKeyboard(this.input);
    bindSwipes(this.input, canvas);
    createRadioBar(ui, this.radio);
    unlockAudioOnGesture(() => this.radio.turnOn());
    bindHotkeys(this.radio);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.mode === 'running') this.pauseRun();
    });
    // A different route each time the game opens, so not everyone is always on the same line.
    this.save({ ...this.profile, routeId: shuffleRoute(SHUFFLE_ROUTE_IDS, this.profile.routeId) });
    setActiveRoute(routeById(this.profile.routeId));
    this.run = new Run(1, this.vehicle());
    this.refreshTrotro();
    this.loop = new Loop({ update: (dt) => this.update(dt), render: () => this.render() });
  }

  private garageDeps(): ConstructorParameters<typeof GarageController>[0] {
    return {
      profile: () => this.profile,
      setProfile: (p) => this.save(p),
      playerKey: () => this.sync.key,
      refreshTrotro: () => this.refreshTrotro(),
    };
  }

  private menuActions(): MenuActions {
    return {
      start: () => this.startRun(),
      garage: () => this.openGarage(),
      leaders: () => this.openLeaders(),
      pickVehicle: (id) => this.pickVehicle(id),
      pickRoute: (id) => this.pickRoute(id),
      shuffleRoute: () => this.shuffleRoute(),
      pickTraffic: (level) => this.save({ ...this.profile, traffic: level }),
      playDaily: () => this.startRun('daily'),
      me: () => this.openMe(),
      acceptChallenge: () => this.startRun('friend'),
      dismissChallenge: () => this.challenges.dismiss(),
    };
  }

  start(): void {
    this.menu.showWith(this.profile);
    this.loop.start();
    void this.sync.connect().then((me) => {
      if (!me) return;
      // The server's wallet and garage win over the local copy.
      this.save({ ...this.profile, walletPesewas: me.wallet.balancePesewas, owned: me.owned, name: me.name });
      if (this.mode === 'menu') this.menu.showWith(this.profile);
      void this.challenges.refreshUnseen(this.sync.key);
    });
    void fetchChampion().then((champion) => this.menu.setChampion(champion));
    void this.challenges.refreshDaily();
    void this.challenges.openLink();
    this.loadAds();
  }

  private save(profile: Profile): void {
    this.profile = profile;
    saveProfile(profile);
  }

  /** The trotro for a shift: your own, or the one a challenge decides (without upgrades, for fair scores). */
  private vehicle(plan: RunPlan | null = null): RunVehicle {
    const id = plan?.vehicle ?? this.profile.vehicle;
    const spec = PLAYER_SPECS[id];
    return {
      capacity: PLAYER_VEHICLES[id].capacity,
      size: { length: spec.length, width: spec.width, height: spec.height },
      upgrades: plan?.useUpgrades === false ? undefined : upgradeLevels(garageState(this.profile)),
      trafficDensity: plan?.traffic ?? trafficDensity(this.profile.traffic),
      conditions: plan?.conditions,
      fuelStart: plan?.fuelStart,
      // Touch screens get sharp lane changes and quicker acceleration; keyboard players keep the smooth glide.
      touchDriving: matchMedia('(pointer: coarse)').matches,
    };
  }

  private refreshTrotro(): void {
    const look = trotroLook(this.profile);
    this.view.setPlayer(this.profile.vehicle, look.sticker, look.paint);
  }

  private pickVehicle(id: PlayerVehicleId): void {
    this.save({ ...this.profile, vehicle: id });
    this.refreshTrotro();
    this.resetWorld();
  }

  private pickRoute(id: string): void {
    this.save({ ...this.profile, routeId: routeById(id).id });
    this.resetWorld();
  }

  private shuffleRoute(): void {
    this.pickRoute(shuffleRoute(SHUFFLE_ROUTE_IDS, this.profile.routeId));
    this.menu.showWith(this.profile);
  }

  /** Loads which real ads are booked on the current route (vacant boards show "your ad here"). */
  private loadAds(route = this.profile.routeId): void {
    this.view.setAds([]);
    void fetchPlacements(route).then((placements) => {
      if ((this.plan?.routeId ?? this.profile.routeId) === route || this.profile.routeId === route) this.view.setAds(placements);
    });
  }

  /** Rebuilds the idle menu scene for the current route and trotro. */
  private resetWorld(): void {
    setActiveRoute(routeById(this.profile.routeId));
    this.loadAds();
    this.run.dispose();
    this.run = new Run(1, this.vehicle());
    this.view.reset();
    this.view.setConditions(CLEAR_DAY);
  }

  private openGarage(): void {
    this.mode = 'garage';
    this.menu.hide();
    this.garage.open(garageState(this.profile), this.profile.walletPesewas);
  }

  private openMe(): void {
    this.mode = 'badges';
    this.menu.hide();
    void this.me.open();
  }

  private openLeaders(): void {
    this.mode = 'leaders';
    this.menu.hide();
    this.leaders.open(this.profile.name);
  }

  private backToMenu(): void {
    this.garage.hide();
    this.leaders.hide();
    this.me.hide();
    this.mode = 'menu';
    this.engine.stop();
    this.menu.showWith(this.profile);
    void this.challenges.refreshDaily();
    void this.challenges.refreshUnseen(this.sync.key);
  }

  private async saveName(name: string): Promise<string | null> {
    const error = await setName(this.sync.key, name);
    if (!error) this.save({ ...this.profile, name });
    return error;
  }

  /** Starts a shift: your own, today's daily challenge, or a friend's challenge (same road and seed as theirs). */
  private startRun(kind: RunKind = 'normal'): void {
    const plan = planRun(kind, this.profile, Date.now(), this.challenges.friend);
    this.plan = plan;
    setActiveRoute(routeById(plan.routeId));
    this.run.dispose();
    this.run = new Run(plan.seed, this.vehicle(plan));
    this.view.reset();
    if (plan.vehicle !== this.profile.vehicle) this.view.setPlayer(plan.vehicle, trotroLook(this.profile).sticker, trotroLook(this.profile).paint);
    this.loadAds(plan.routeId);
    this.view.setConditions(plan.conditions);
    this.hud.setConditions(plan.conditions);
    this.feedback.announceConditions(plan.conditions);
    this.rival.set(plan.rival);
    if (plan.kind === 'daily') void this.challenges.dailyRival().then((rival) => this.plan === plan && this.rival.set(rival));
    this.feedback.reset();
    this.feedback.bind(this.run.events);
    this.run.events.on('run:collision', () => this.view.chase.kick(1.2));
    this.run.events.on('run:stumbled', () => this.view.chase.kick(0.5));
    this.run.events.on('run:brokeDown', () => this.onBreakdown());
    this.run.events.on('journey:arrived', () => this.onArrival());
    this.run.events.on('fuel:low', (e) => playFuelWarning(e.level));
    this.run.events.on('run:horn', () => playHorn(this.profile.equipped.horn));
    this.shiftFinished = false;
    this.lastResult = null;
    this.nextProgressAt = PROGRESS_EVERY_SECONDS;
    this.hud.startShift(this.profile.walletPesewas);
    this.sync.startShift(plan.vehicle, plan.routeId, plan.start);
    this.input.releaseAll();
    this.menu.hide();
    this.over.hide();
    this.hud.setVisible(true);
    this.view.startAdTracking();
    this.mode = 'running';
    this.engine.setVehicle(plan.vehicle);
    this.engine.start();
  }

  private update(dt: number): void {
    const actions = this.input.consume();
    if (this.mode === 'running') {
      if (actions.pause) return this.pauseRun();
      this.run.update(dt, actions);
      this.maybeSendProgress();
    } else if (this.mode === 'crashed') {
      this.crashTimer -= dt;
      if (this.crashTimer <= 0) this.finishRun();
    } else if (this.mode === 'paused' && actions.pause) {
      this.resume();
    } else if (this.mode === 'menu' || this.mode === 'garage' || this.mode === 'leaders') {
      this.run.update(0, emptyActions());
    }
  }

  private render(): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastRender) / 1000);
    this.lastRender = now;
    this.perf.frame(dt);
    this.view.render(this.run.state, dt);
    if (this.mode === 'running') this.engine.update(this.run.state.bus.speed, dt);
    if (this.mode === 'running' || this.mode === 'crashed') {
      this.radio.setEnergy(Math.min(1, this.run.state.bus.speed / 30));
      this.hud.update(this.run.state);
      this.rival.update(this.run.state.stats.score);
      this.prompt.update(this.run.state);
      this.dashPrompt.update(this.run.state);
      this.signal.update(this.run.state);
      this.powerChips.update(this.run.state);
      this.feedback.update(dt);
    }
  }

  /** Every minute of driving, credit what's been earned so far and update the live leaderboard. */
  private maybeSendProgress(): void {
    const { state } = this.run;
    if (state.over || state.stats.seconds < this.nextProgressAt) return;
    this.nextProgressAt = state.stats.seconds + PROGRESS_EVERY_SECONDS;
    void this.sync.progress(state).then((result) => {
      if (result?.accepted) this.hud.setRank(result.ranks.day);
    });
  }

  /** The trotro is wrecked: a short pause on the scene, then the shift summary. */
  private onBreakdown(): void {
    this.mode = 'crashed';
    this.engine.stop();
    this.crashTimer = CRASH_PAUSE;
    this.view.chase.kick(1.4);
  }

  /** A one-way journey is over: the trotro sits at the end of the road for a moment, then the summary. */
  private onArrival(): void {
    this.mode = 'crashed';
    this.engine.stop();
    this.crashTimer = 2.2;
  }

  private endShift(): void {
    if (this.mode !== 'paused') return;
    this.pause.hide();
    this.run.endShift();
    this.finishRun();
  }

  private finishRun(): void {
    if (this.shiftFinished) return;
    this.shiftFinished = true;
    const settled = settleShift(this.profile, this.run.state, Date.now(), missionReward, this.plan?.kind !== 'daily' && this.plan?.kind !== 'friend');
    void reportImpressions(this.sync.key, this.view.stopAdTracking());
    this.save(settled.profile);
    this.mode = 'over';
    this.engine.stop();
    this.hud.setVisible(false);
    this.over.showWith(this.run.state, this.profile.bestScore, this.profile.walletPesewas, this.plan !== null && this.plan.kind !== 'normal');
    this.rival.set(null);
    this.over.askName(this.profile.name !== null);
    this.overMissions.render(this.profile.missions, null);
    void this.sync.finishShift(this.run.state, settled.completed).then((result) => {
      if (!result) return this.over.offline();
      this.save({ ...this.profile, walletPesewas: result.wallet.balancePesewas, bestScore: Math.max(this.profile.bestScore, result.best) });
      this.over.confirm(result.wallet.balancePesewas, result.ranks.day, result.accepted);
      this.lastResult = result;
      this.over.extras.confirm(result, this.plan?.kind ?? 'normal');
    });
  }

  private pauseRun(): void {
    this.mode = 'paused';
    this.engine.stop();
    this.input.releaseAll();
    this.pause.show();
  }

  private resume(): void {
    this.pause.hide();
    this.mode = 'running';
    this.engine.setVehicle(this.profile.vehicle);
    this.engine.start();
  }

  private toMenu(): void {
    // Quitting mid-shift still settles it, so fines and repairs can't be dodged.
    if (this.mode === 'paused' && !this.shiftFinished) {
      this.run.endShift();
      this.finishRun();
    }
    this.pause.hide();
    this.over.hide();
    this.hud.setVisible(false);
    this.plan = null;
    this.rival.set(null);
    this.refreshTrotro();
    this.resetWorld();
    this.mode = 'menu';
    this.engine.stop();
    this.menu.showWith(this.profile);
    void this.challenges.refreshDaily();
    void this.challenges.refreshUnseen(this.sync.key);
  }
}
